import { useState, useEffect } from 'react';

const WHATSAPP_URL = 'https://wa.me/5519988179126?text=Ol%C3%A1!%20Gostaria%20de%20agendar%20uma%20visita%20%C3%A0%20Escola%20Turminha%20da%20Alegria.';

interface Slide {
  bg: string;
  bgMobile?: string;
  bgPosition: string;
  bgPositionMobile?: string;
  bgSize: string;
  title: string;
  subtitle: string;
  cta: string;
  ctaLink: string;
  ctaStyle: string;
  external?: boolean;
}

const slides: Slide[] = [
  {
    bg: 'https://storage.readdy-site.link/project_files/f6b15bb9-0a9f-496b-9ad9-42176d58e2ec/82db267d-131a-4941-9a14-e6c418872047_Start-1.jpg?v=1a9b5a7364e029dfb676fb090bd92580',
    bgPosition: 'center 45%',
    bgSize: 'cover',
    title: 'Confiança para os pais,\nalegria para as crianças',
    subtitle: 'Ambiente acolhedor com princípios e valores cristãos',
    cta: 'Falar no WhatsApp',
    ctaLink: WHATSAPP_URL,
    ctaStyle: 'bg-azul-escuro',
    external: true,
  },
  {
    bg: 'https://storage.readdy-site.link/project_files/f6b15bb9-0a9f-496b-9ad9-42176d58e2ec/48d09e27-e604-4a11-8ccc-4af4e316bfdd_20251110_094257.jpg?v=0bfcd7a45599782f27bef039a5c9ffc7',
    bgPosition: 'center top',
    bgSize: 'cover',
    title: 'Aprender brincando\ntodos os dias',
    subtitle: 'Desenvolvimento completo com atividades educativas e recreativas',
    cta: 'Conhecer a escola',
    ctaLink: '#sobre',
    ctaStyle: 'bg-azul-medio',
  },
  {
    bg: 'https://storage.readdy-site.link/project_files/f6b15bb9-0a9f-496b-9ad9-42176d58e2ec/b0388a9f-04ca-42e5-aca3-77453686a1e3_turminha-horizontal.jpg?v=874a9c789168e61a766265e5ebbd29b9',
    bgPosition: 'center center',
    bgSize: 'cover',
    bgMobile: 'https://storage.readdy-site.link/project_files/f6b15bb9-0a9f-496b-9ad9-42176d58e2ec/3b327017-a85b-43af-bb4d-253aa9c948d4_turminha-vertical.jpg?v=a46f7a6d1172be1fb8a74ca098ede013',
    bgPositionMobile: 'center center',
    title: 'Um lugar cheio de amor\ne cuidado para seu filho',
    subtitle: 'Educação infantil em Campinas com carinho, segurança e aprendizado',
    cta: 'Agendar visita',
    ctaLink: '#contato',
    ctaStyle: 'bg-vermelho',
  },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current]);

  const goTo = (index: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 300);
  };

  const slide = slides[current];

  const handleCta = () => {
    if (slide.external) {
      window.open(slide.ctaLink, '_blank');
    } else {
      const el = document.querySelector(slide.ctaLink);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Background Images */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Desktop background */}
          <div
            className={s.bgMobile ? 'hidden md:block absolute inset-0' : 'absolute inset-0'}
            style={{
              backgroundImage: `url(${s.bg})`,
              backgroundSize: s.bgSize,
              backgroundPosition: s.bgPosition,
            }}
          />
          {/* Mobile background (only when bgMobile is defined) */}
          {s.bgMobile && (
            <div
              className="block md:hidden absolute inset-0"
              style={{
                backgroundImage: `url(${s.bgMobile})`,
                backgroundSize: 'cover',
                backgroundPosition: s.bgPositionMobile ?? 'center center',
              }}
            />
          )}
        </div>
      ))}

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />

      {/* Content */}
      <div
        className={`relative z-10 h-full flex flex-col items-center justify-center text-center px-6 transition-all duration-500 ${
          animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        }`}
      >
        <div className="max-w-4xl w-full mx-auto">
          <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 drop-shadow-lg whitespace-pre-line">
            {slide.title}
          </h2>
          <p className="font-body text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto drop-shadow">
            {slide.subtitle}
          </p>
          <button
            onClick={handleCta}
            className={`${slide.ctaStyle} text-white font-body font-700 text-base px-8 py-4 rounded-full hover:opacity-90 hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-lg`}
          >
            {slide.cta}
          </button>
        </div>
      </div>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-0 w-full flex justify-center gap-3 z-20">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? 'w-8 h-3 bg-vermelho'
                : 'w-3 h-3 bg-white/60 hover:bg-white/90'
            }`}
          />
        ))}
      </div>

      {/* Arrow Navigation */}
      <button
        onClick={() => goTo((current - 1 + slides.length) % slides.length)}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 text-white rounded-full transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={() => goTo((current + 1) % slides.length)}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 text-white rounded-full transition-all cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>
    </section>
  );
}
